import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable react-refresh/only-export-components */
import { AlertCircle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useState, } from 'react';
import { cn } from '@/lib/utils';
const ToastCtx = createContext(null);
const DEFAULTS = {
    success: 4000,
    info: 4000,
    warning: 6000,
    error: 8000,
};
export function ToastProvider({ children }) {
    const [items, setItems] = useState([]);
    const dismiss = useCallback((id) => {
        setItems((xs) => xs.filter((x) => x.id !== id));
    }, []);
    const push = useCallback((t) => {
        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        const item = { id, duration: DEFAULTS[t.tone], ...t };
        setItems((xs) => [...xs, item]);
        return id;
    }, []);
    const api = {
        push, dismiss,
        success: (title, description) => push({ tone: 'success', title, description }),
        error: (title, description) => push({ tone: 'error', title, description }),
        info: (title, description) => push({ tone: 'info', title, description }),
        warning: (title, description) => push({ tone: 'warning', title, description }),
    };
    return (_jsxs(ToastCtx.Provider, { value: api, children: [children, _jsx("div", { className: "pointer-events-none fixed inset-x-0 bottom-4 z-[100] flex flex-col items-center gap-2 px-4 sm:bottom-6 sm:items-end sm:px-6", children: items.map((t) => (_jsx(ToastView, { item: t, onDismiss: () => dismiss(t.id) }, t.id))) })] }));
}
export function useToast() {
    const ctx = useContext(ToastCtx);
    if (ctx === null)
        throw new Error('useToast must be used within ToastProvider');
    return ctx;
}
function ToastView({ item, onDismiss }) {
    const [leaving, setLeaving] = useState(false);
    useEffect(() => {
        if (!item.duration)
            return;
        const t = window.setTimeout(() => {
            setLeaving(true);
            window.setTimeout(onDismiss, 200);
        }, item.duration);
        return () => window.clearTimeout(t);
    }, [item.duration, onDismiss]);
    const Icon = TONE_ICONS[item.tone];
    return (_jsxs("div", { role: "status", className: cn('pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg border bg-card p-3 shadow-lg ring-1 ring-black/5 backdrop-blur', 'transition-all duration-200', leaving ? 'translate-y-1 opacity-0' : 'translate-y-0 opacity-100', TONE_RING[item.tone]), children: [_jsx("span", { className: cn('mt-0.5 shrink-0', TONE_FG[item.tone]), children: _jsx(Icon, { className: "h-4 w-4" }) }), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("p", { className: "text-sm font-medium", children: item.title }), item.description && (_jsx("p", { className: "mt-0.5 text-xs text-muted-foreground", children: item.description }))] }), _jsx("button", { type: "button", onClick: () => { setLeaving(true); setTimeout(onDismiss, 150); }, className: "rounded p-0.5 text-muted-foreground hover:bg-muted", "aria-label": "Dismiss", children: _jsx(X, { className: "h-3.5 w-3.5" }) })] }));
}
const TONE_ICONS = {
    success: CheckCircle2,
    error: XCircle,
    info: Info,
    warning: AlertCircle,
};
const TONE_FG = {
    success: 'text-emerald-600',
    error: 'text-red-600',
    info: 'text-blue-600',
    warning: 'text-amber-600',
};
const TONE_RING = {
    success: 'ring-emerald-500/20',
    error: 'ring-red-500/20',
    info: 'ring-blue-500/20',
    warning: 'ring-amber-500/20',
};
